import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { KENO_API, gameHeaders } from '../keno/fairnessApi';
import { formatApiErrorDetail } from '../../auth';

export const useTipTransfer = (userId) => {
  const key = `gv_pending_tip_${userId}`;
  const [wallet, setWallet] = useState(null), [proposal, setProposal] = useState(null), [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [pending, setPending] = useState(() => {
    try { return JSON.parse(localStorage.getItem(key)) || null; } catch { return null; }
  });
  const sending = useRef(false), mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const loadWallet = useCallback(async () => {
    const { data } = await axios.get(`${KENO_API}/keno/wallet`, { headers: gameHeaders(false) });
    if (mounted.current) setWallet(data);
    return data;
  }, []);
  useEffect(() => { loadWallet().catch(() => { if (mounted.current) setError('Unable to load your playing balance.'); }); }, [loadWallet]);
  const accept = useCallback((data) => {
    window.dispatchEvent(new CustomEvent('gv-wallet-updated', { detail: data.wallet }));
    if (data.receipt.status === 'completed') window.dispatchEvent(new CustomEvent('gv-tip-sent', { detail: { user_id: userId, receipt: data.receipt } }));
    if (data.receipt.status !== 'pending') localStorage.removeItem(key);
    if (!mounted.current) return;
    setWallet(data.wallet); setReceipt(data.receipt); setError(''); setProposal(null);
    if (data.receipt.status !== 'pending') setPending(null);
  }, [key, userId]);
  const deliver = useCallback(async (payload, checkOnly = false) => {
    if (sending.current) return;
    sending.current = true; if (mounted.current) { setBusy(true); setError(''); }
    try {
      let data;
      if (checkOnly) {
        try { data = (await axios.get(`${KENO_API}/tips/${payload.request_id}`, { headers: gameHeaders(false) })).data; }
        catch (failure) { if (failure.response?.status !== 404) throw failure; }
      }
      if (!data) data = (await axios.post(`${KENO_API}/tips`, payload, { headers: gameHeaders(false) })).data;
      accept(data);
    } catch (failure) {
      const knownFailure = failure.response && failure.response.status < 500;
      if (knownFailure) localStorage.removeItem(key);
      if (mounted.current) {
        setError(knownFailure ? formatApiErrorDetail(failure.response.data?.detail || 'Unable to send this tip.') : 'Confirmation delayed. Checking the same tip—do not send again.');
        if (knownFailure) { setPending(null); setProposal(null); }
      }
    } finally { sending.current = false; if (mounted.current) setBusy(false); }
  }, [accept, key]);
  useEffect(() => {
    if (!pending) return;
    deliver(pending, true);
    const timer = setInterval(() => deliver(pending, true), 3000);
    return () => clearInterval(timer);
  }, [pending, deliver]);
  const review = async (amountMinor, recipient, username) => {
    if (busy || sending.current || pending) return;
    setBusy(true); setError(''); setReceipt(null);
    try {
      const [balance, result] = await Promise.all([loadWallet(), recipient
        ? axios.get(`${KENO_API}/community/profiles/${recipient.user_id}`, { headers: gameHeaders(false) })
        : axios.get(`${KENO_API}/tips/recipient`, { headers: gameHeaders(false), params: { username: username.trim() } })]);
      if (result.data.user_id === userId) throw new Error('You cannot tip yourself.');
      if (amountMinor > balance.balance_minor) throw new Error('Insufficient playing balance.');
      if (mounted.current) setProposal({ recipient_username: result.data.username, payload: { request_id: crypto.randomUUID(), recipient_id: result.data.user_id, amount_minor: amountMinor, revision: balance.revision, confirmed: true } });
    } catch (failure) { if (mounted.current) setError(formatApiErrorDetail(failure.response?.data?.detail || failure.message || 'Unable to prepare tip.')); }
    finally { if (mounted.current) setBusy(false); }
  };
  const confirm = () => {
    if (!proposal || busy || pending) return;
    localStorage.setItem(key, JSON.stringify(proposal.payload));
    setPending(proposal.payload);
    deliver(proposal.payload);
  };
  return { wallet, proposal, receipt, busy, error, pending, review, confirm,
    cancel: () => setProposal(null), reset: () => { setReceipt(null); setError(''); } };
};